import { horizontalListSortingStrategy, SortableContext } from '@dnd-kit/sortable';
import { css } from '@emotion/react';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { Chip, Text } from '@frontend/design-system';
import { CALENDAR_Z_INDICES } from '../../constants';
import { useCalendarGridStore } from '../../store/calendar-grid.store';
import { CalendarColumnGroup, CalendarColumnHeaderType } from '../../types';
import { listItemSideSeparatorStyle } from '../styles';
import { ColumnHeader } from './column-header';
import { SortableColumnHeader } from './sortable-column-header';

interface Props {
  groupInfo: CalendarColumnGroup;
  gridColumn: string;
  isGroupHeaderVisible: boolean;
  headerType: CalendarColumnHeaderType;
  columnIds: string[];
}

export const GroupHeader = ({ groupInfo, gridColumn, isGroupHeaderVisible, headerType, columnIds }: Props) => {
  const isColumnReorderEnabled = useCalendarGridStore((s) => s.isColumnReorderEnabled);

  const groupNameSpan = isGroupHeaderVisible && (
    <div css={groupNameContainerStyle} style={{ gridColumn: `1 / span ${groupInfo.columnIds.length}` }}>
      <div>
        <div>
          <Icon name='location' color='subdued' size={16} />
          <Text as='span' color='subdued' size='small' weight='bold'>
            {groupInfo.name}
          </Text>
        </div>
        {!!groupInfo.timezone && <Chip.TimeZone>{groupInfo.timezone}</Chip.TimeZone>}
      </div>
    </div>
  );

  if (!isColumnReorderEnabled) {
    return (
      <header data-group-header css={containerStyle} style={{ gridColumn }}>
        {groupNameSpan}
        {columnIds.map((columnId) => (
          <ColumnHeader key={columnId} columnId={columnId} headerType={headerType} groupId={groupInfo.id} />
        ))}
      </header>
    );
  }

  return (
    <SortableContext items={columnIds} strategy={horizontalListSortingStrategy}>
      <header data-group-header css={containerStyle} style={{ gridColumn }}>
        {groupNameSpan}
        {columnIds.map((columnId) => (
          <SortableColumnHeader
            key={columnId}
            columnId={columnId}
            headerType={headerType}
            groupId={groupInfo.id}
            columnIds={columnIds}
          />
        ))}
      </header>
    </SortableContext>
  );
};

const containerStyle = css({
  display: 'grid',
  gridTemplateColumns: 'subgrid',
  gridRow: 1,
  position: 'sticky',
  top: 0,
  zIndex: CALENDAR_Z_INDICES.groupHeader,
  boxShadow: theme.shadows.light,
  backgroundColor: theme.colors.white,
  '> [role="button"]': listItemSideSeparatorStyle,
});

const groupNameContainerStyle = css({
  display: 'flex',
  justifyContent: 'center',
  borderBottom: `1px solid ${theme.colors.neutral20}`,
  '> div': {
    backgroundColor: theme.colors.neutral5,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: theme.spacing(1),
    minWidth: 300,
    maxWidth: 'calc(var(--viewport-width) - var(--time-column-width))',
    width: '100%',
    textAlign: 'center',
    position: 'sticky',
    left: 'var(--time-column-width)',
    right: 0,
    '> div': {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(0.5),
    },
  },
});
